import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { NAV, ABOUT_LINKS } from "@/data/site";
import { Menu, X, Languages, ShieldCheck, ChevronDown, Upload } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { toast } from "sonner";

export function BrandLogo({ className = "h-14 sm:h-16" }) {
  const { editMode, ov, saveImage } = useApp();
  const inputRef = useRef(null);
  const overridden = ov("site.logo");
  const src = overridden !== undefined ? fileUrl(overridden) : "/assets/oojjas-logo-tagline.jpg";

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { await saveImage("site.logo", file); toast.success("Logo updated"); }
    catch (err) { toast.error("Logo upload failed"); console.error(err); }
    finally { e.target.value = ""; }
  };

  return (
    <div className="relative bg-white rounded-xl px-2.5 py-1.5 shadow-lg border border-amber-400/50">
      <img src={src} alt="OOJJAS Corporate Private Limited" className={`${className} w-auto object-contain`} />
      <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-teal-500 pulse-dot" />
      {editMode && (
        <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); inputRef.current?.click(); }}
          className="absolute inset-0 grid place-items-center rounded-xl bg-[#017471]/85 text-white opacity-0 hover:opacity-100 transition-opacity" data-testid="edit-logo">
          <span className="flex items-center gap-1 text-[10px] font-bold"><Upload className="w-3.5 h-3.5" /> Logo</span>
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
    </div>
  );
}

export default function Header() {
  const { t, lang, toggleLang, ov, pages } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const visibleNav = NAV.filter((n) => ov(`navhide.${n.key}`) !== "1");
  const primary = visibleNav.slice(0, 6);
  const secondary = visibleNav.slice(6);
  const customNav = (pages || []).filter((p) => p.nav);
  const pageLabel = (p) => (lang === "hi" ? (p.title_hi || p.title) : p.title);

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? "shadow-2xl" : ""}`} style={{ backdropFilter: "blur(18px)" }} data-testid="site-header">
      <div className="bg-gradient-to-r from-[#014845] via-[#015E5C] to-[#014845] border-b border-amber-400/25">
      <div className="oj-container h-[84px] flex items-center justify-between gap-4">
        <Link to="/" data-testid="header-logo-link" className="shrink-0"><BrandLogo className="h-12 xl:h-14" /></Link>

        <nav className="hidden xl:flex flex-nowrap flex-1 items-center justify-center gap-1">
          {primary.map((n, idx) => {
            const active = location.pathname === n.path;
            const btn = (
              <Link key={n.path} to={n.path} data-testid={`nav-${n.key}`}
                className={`px-3 py-2 text-[12px] font-bold rounded-full whitespace-nowrap border border-amber-300 bg-gradient-to-b from-amber-300 to-amber-500 text-[#014845] shadow-md shadow-amber-900/20 hover:from-amber-200 hover:to-amber-400 transition-all duration-200 ${active ? "ring-2 ring-amber-200/80 ring-offset-1 ring-offset-[#015E5C]" : ""}`}>
                {t.nav[n.key]}
              </Link>
            );
            if (n.key !== "home") return btn;
            return (
              <div key="home-about" className="flex items-center gap-1">
                {btn}
                <div className="relative group">
                  <Link to="/about" data-testid="nav-about"
                    className={`px-3 py-2 text-[12px] font-bold rounded-full whitespace-nowrap border border-amber-300 bg-gradient-to-b from-amber-300 to-amber-500 text-[#014845] shadow-md hover:from-amber-200 hover:to-amber-400 flex items-center gap-1 transition-all ${location.pathname === "/about" ? "ring-2 ring-amber-200/80 ring-offset-1 ring-offset-[#015E5C]" : ""}`}>
                    {t.aboutUs} <ChevronDown className="w-3.5 h-3.5" />
                  </Link>
                  <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                    <div className="oj-card p-2 w-60">
                      {ABOUT_LINKS.map(([label, path], i) => (
                        <Link key={path} to={path} data-testid={`nav-about-${label.replace(/\W+/g, "-").toLowerCase()}`}
                          className="block px-3 py-2 text-sm rounded-lg text-slate-600 hover:text-[#017471] hover:bg-teal-50">
                          {t.aboutLinks[i] || label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          <div className="relative group">
            <button className="px-3 py-2 text-[12px] font-bold rounded-full whitespace-nowrap border border-amber-300 bg-gradient-to-b from-amber-300 to-amber-500 text-[#014845] shadow-md hover:from-amber-200 hover:to-amber-400 flex items-center gap-1 transition-all" data-testid="nav-more">
              {t.common.explore} <ChevronDown className="w-3.5 h-3.5" />
            </button>
            <div className="absolute right-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
              <div className="oj-card p-2 w-52">
                {secondary.map((n) => (
                  <Link key={n.path} to={n.path} data-testid={`nav-${n.key}`}
                    className="block px-3 py-2 text-sm rounded-lg text-slate-600 hover:text-[#017471] hover:bg-teal-50">
                    {t.nav[n.key]}
                  </Link>
                ))}
                {customNav.map((p) => (
                  <Link key={p.slug} to={`/p/${p.slug}`} data-testid={`nav-page-${p.slug}`}
                    className="block px-3 py-2 text-sm rounded-lg text-slate-600 hover:text-[#017471] hover:bg-teal-50">
                    {pageLabel(p)}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </nav>

        <div className="flex items-center gap-2">
          <button onClick={toggleLang} data-testid="nav-language-switcher"
            className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-semibold text-white border border-white/30 bg-white/10 hover:bg-white/20 transition-all">
            <Languages className="w-4 h-4" /> {t.langLabel}
          </button>
          <button onClick={() => navigate("/access-portal")} data-testid="header-portal-btn" className="oj-btn-gold !px-4 !py-2.5 !text-[13px]">
            <ShieldCheck className="w-4 h-4" /> <span className="hidden sm:inline">{t.portal}</span>
          </button>
          <button className="xl:hidden p-2 text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile quick nav — always-visible scrollable gold buttons */}
      <div className="xl:hidden overflow-x-auto no-scrollbar bg-[#015E5C] border-t border-white/10" data-testid="mobile-quick-nav">
        <div className="flex items-center gap-2 px-3 py-2 w-max">
          <Link to="/" data-testid="mnav-home"
            className={`px-3.5 py-1.5 text-[12px] font-bold rounded-full border border-amber-300 bg-gradient-to-b from-amber-300 to-amber-500 text-[#014845] whitespace-nowrap ${location.pathname === "/" ? "ring-2 ring-amber-200/80" : ""}`}>
            {t.nav.home}
          </Link>
          <Link to="/about" data-testid="mnav-about"
            className={`px-3.5 py-1.5 text-[12px] font-bold rounded-full border border-amber-300 bg-gradient-to-b from-amber-300 to-amber-500 text-[#014845] whitespace-nowrap ${location.pathname === "/about" ? "ring-2 ring-amber-200/80" : ""}`}>
            {t.aboutUs}
          </Link>
          {visibleNav.filter((n) => n.key !== "home").map((n) => (
            <Link key={n.path} to={n.path} data-testid={`mnav-${n.key}`}
              className={`px-3.5 py-1.5 text-[12px] font-bold rounded-full border border-amber-300 bg-gradient-to-b from-amber-300 to-amber-500 text-[#014845] whitespace-nowrap ${location.pathname === n.path ? "ring-2 ring-amber-200/80" : ""}`}>
              {t.nav[n.key]}
            </Link>
          ))}
          {customNav.map((p) => (
            <Link key={p.slug} to={`/p/${p.slug}`} data-testid={`mnav-page-${p.slug}`}
              className={`px-3.5 py-1.5 text-[12px] font-bold rounded-full border border-amber-300 bg-gradient-to-b from-amber-300 to-amber-500 text-[#014845] whitespace-nowrap ${location.pathname === `/p/${p.slug}` ? "ring-2 ring-amber-200/80" : ""}`}>
              {pageLabel(p)}
            </Link>
          ))}
        </div>
      </div>

      {open && (
        <div className="xl:hidden border-t border-white/15 bg-[#014845] px-4 py-4 grid grid-cols-2 gap-2" data-testid="mobile-menu">
          {visibleNav.map((n) => (
            <Link key={n.path} to={n.path} data-testid={`mobile-nav-${n.key}`}
              className={`px-4 py-2.5 rounded-full text-center text-[13px] font-bold border border-amber-300 bg-gradient-to-b from-amber-300 to-amber-500 text-[#014845] transition-all ${location.pathname === n.path ? "ring-2 ring-amber-200/80" : ""}`}>
              {t.nav[n.key]}
            </Link>
          ))}
          {customNav.map((p) => (
            <Link key={p.slug} to={`/p/${p.slug}`} data-testid={`mobile-nav-page-${p.slug}`}
              className="px-4 py-2.5 rounded-full text-center text-[13px] font-bold border border-amber-300 bg-gradient-to-b from-amber-300 to-amber-500 text-[#014845]">
              {pageLabel(p)}
            </Link>
          ))}
          <Link to="/about" data-testid="mobile-nav-about" className="col-span-2 px-4 py-2.5 rounded-full text-center text-[13px] font-bold border border-amber-300 bg-gradient-to-b from-amber-300 to-amber-500 text-[#014845]">
            {t.aboutUs}
          </Link>
          {ABOUT_LINKS.map(([label, path], i) => (
            <Link key={path} to={path} className="px-3 py-2 rounded-lg text-center text-[12px] font-medium text-teal-50 bg-white/5 border border-white/15">
              {t.aboutLinks[i] || label}
            </Link>
          ))}
          <button onClick={toggleLang} data-testid="mobile-language-switcher" className="col-span-2 mt-1 px-4 py-2.5 rounded-full text-amber-300 font-semibold border border-amber-400/40 bg-amber-400/10 flex items-center justify-center gap-2">
            <Languages className="w-4 h-4" /> {lang === "en" ? "हिंदी में देखें" : "View in English"}
          </button>
        </div>
      )}
      </div>
    </header>
  );
}
